import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Plane, Baby, Users, CalendarRange } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { updateReservation } from "@/lib/api";

const uid = () => Math.random().toString(36).slice(2);

export const ReservationDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [res, setRes] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      const base = client.reservation || {};
      setRes(
        JSON.parse(
          JSON.stringify({
            client_name: base.client_name || client.full_name,
            guests: base.guests || [],
            adults: base.adults || 1,
            children: base.children || [],
            travel_date: base.travel_date || "",
            travel_date_open: base.travel_date_open || false,
            has_flights: base.has_flights || false,
            we_sold_flights: base.we_sold_flights || false,
            flights: base.flights || [],
            notes: base.notes || "",
          })
        )
      );
    }
  }, [client]);

  if (!res || !client) return null;

  const set = (k, v) => setRes((r) => ({ ...r, [k]: v }));

  const addGuest = () => set("guests", [...res.guests, { id: uid(), name: "" }]);
  const setGuest = (id, name) =>
    set("guests", res.guests.map((g) => (g.id === id ? { ...g, name } : g)));
  const removeGuest = (id) => set("guests", res.guests.filter((g) => g.id !== id));

  const setChildrenCount = (n) => {
    const count = Math.max(0, parseInt(n) || 0);
    const current = [...res.children];
    if (count > current.length) {
      while (current.length < count) current.push({ id: uid(), age: 0 });
    } else {
      current.length = count;
    }
    set("children", current);
  };
  const setChildAge = (id, age) =>
    set("children", res.children.map((c) => (c.id === id ? { ...c, age: parseInt(age) } : c)));

  const addFlight = () =>
    set("flights", [
      ...res.flights,
      { id: uid(), airline: "", departure_time: "", arrival_time: "", date: "", flight_number: "", pnr: "" },
    ]);
  const setFlight = (id, k, v) =>
    set("flights", res.flights.map((f) => (f.id === id ? { ...f, [k]: v } : f)));
  const removeFlight = (id) => set("flights", res.flights.filter((f) => f.id !== id));

  const showFlights = res.has_flights || res.we_sold_flights;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateReservation(client.id, res);
      toast.success("Reserva actualizada");
      onSaved?.(updated);
      onOpenChange(false);
    } catch {
      toast.error("No se pudo guardar la reserva");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0" data-testid="reservation-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <DialogTitle className="font-heading text-xl">Datos de la Reserva</DialogTitle>
          <DialogDescription>
            {client.client_code} · Configura fecha, huéspedes, edades y vuelos.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh]">
          <div className="px-6 py-5 space-y-6">
            <div className="grid gap-1.5">
              <Label>Nombre del cliente</Label>
              <Input
                data-testid="reservation-client-name"
                value={res.client_name}
                onChange={(e) => set("client_name", e.target.value)}
              />
            </div>

            {/* Fecha de viaje */}
            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <div className="flex items-center gap-2 text-slate-700 font-medium">
                <CalendarRange className="h-4 w-4" /> Fecha de viaje
              </div>
              <div className="grid gap-1.5">
                <Input
                  type="date"
                  data-testid="travel-date-input"
                  value={res.travel_date}
                  disabled={res.travel_date_open}
                  onChange={(e) => set("travel_date", e.target.value)}
                  className={res.travel_date_open ? "opacity-50" : ""}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="opendate" className="font-normal text-sm text-slate-600">
                  Fecha abierta (sin fecha definida de viaje)
                </Label>
                <Switch
                  id="opendate"
                  data-testid="travel-date-open-checkbox"
                  checked={res.travel_date_open}
                  onCheckedChange={(v) => {
                    set("travel_date_open", v);
                    if (v) set("travel_date", "");
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label className="flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-slate-500" /> Adultos
                </Label>
                <Input
                  type="number"
                  min={1}
                  data-testid="adults-count-input"
                  value={res.adults}
                  onChange={(e) => set("adults", Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="flex items-center gap-1.5">
                  <Baby className="h-4 w-4 text-slate-500" /> Niños
                </Label>
                <Input
                  type="number"
                  min={0}
                  data-testid="children-count-input"
                  value={res.children.length}
                  onChange={(e) => setChildrenCount(e.target.value)}
                />
              </div>
            </div>

            {res.children.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Edad de cada niño (0 a 17 años)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {res.children.map((c, i) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <span className="text-sm text-slate-600 w-16">Niño {i + 1}</span>
                      <Select value={String(c.age)} onValueChange={(v) => setChildAge(c.id, v)}>
                        <SelectTrigger data-testid={`child-age-select-${i}`} className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {Array.from({ length: 18 }, (_, a) => (
                            <SelectItem key={a} value={String(a)}>
                              {a} {a === 1 ? "año" : "años"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <Label>Huéspedes adicionales</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="add-guest-button"
                  onClick={addGuest}
                  className="h-8"
                >
                  <Plus className="h-4 w-4 mr-1" /> Agregar
                </Button>
              </div>
              {res.guests.map((g, i) => (
                <div key={g.id} className="flex items-center gap-2" data-testid={`guest-row-${i}`}>
                  <Input
                    value={g.name}
                    placeholder={`Huésped ${i + 1}`}
                    onChange={(e) => setGuest(g.id, e.target.value)}
                  />
                  <ConfirmDialog
                    title="¿Eliminar huésped?"
                    description="Se quitará este huésped de la reserva."
                    testId={`confirm-remove-guest-${i}`}
                    onConfirm={() => removeGuest(g.id)}
                    trigger={
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-slate-400 hover:text-red-500 shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    }
                  />
                </div>
              ))}
              {res.guests.length === 0 && (
                <p className="text-sm text-slate-400">Solo el titular de la reserva.</p>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 p-4 space-y-4">
              <div className="flex items-center gap-2 text-slate-700 font-medium">
                <Plane className="h-4 w-4" /> Vuelos
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="hasf" className="font-normal text-sm text-slate-600">
                  El cliente ya cuenta con vuelos
                </Label>
                <Switch
                  id="hasf"
                  data-testid="has-flights-checkbox"
                  checked={res.has_flights}
                  onCheckedChange={(v) => set("has_flights", v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="soldf" className="font-normal text-sm text-slate-600">
                  Nosotros le hemos vendido los vuelos
                </Label>
                <Switch
                  id="soldf"
                  data-testid="sold-flights-checkbox"
                  checked={res.we_sold_flights}
                  onCheckedChange={(v) => set("we_sold_flights", v)}
                />
              </div>

              {showFlights && (
                <div className="space-y-3 pt-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Detalles de vuelos
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="add-flight-button"
                      onClick={addFlight}
                      className="h-8"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Añadir vuelo
                    </Button>
                  </div>
                  {res.flights.map((f, i) => (
                    <div
                      key={f.id}
                      className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2.5"
                      data-testid={`flight-row-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-600">Vuelo {i + 1}</span>
                        <ConfirmDialog
                          title="¿Eliminar vuelo?"
                          description="Se quitará este vuelo de la reserva."
                          testId={`confirm-remove-flight-${i}`}
                          onConfirm={() => removeFlight(f.id)}
                          trigger={
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          }
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <Input
                          data-testid={`flight-airline-input-${i}`}
                          placeholder="Aerolínea"
                          value={f.airline}
                          onChange={(e) => setFlight(f.id, "airline", e.target.value)}
                        />
                        <Input
                          placeholder="N.º de vuelo"
                          value={f.flight_number}
                          onChange={(e) => setFlight(f.id, "flight_number", e.target.value)}
                        />
                        <div className="grid gap-1">
                          <span className="text-xs text-slate-500">Fecha</span>
                          <Input
                            type="date"
                            data-testid={`flight-date-input-${i}`}
                            value={f.date}
                            onChange={(e) => setFlight(f.id, "date", e.target.value)}
                          />
                        </div>
                        <Input
                          placeholder="Código reserva (PNR)"
                          value={f.pnr}
                          onChange={(e) => setFlight(f.id, "pnr", e.target.value)}
                          className="self-end"
                        />
                        <div className="grid gap-1">
                          <span className="text-xs text-slate-500">Salida</span>
                          <Input
                            type="time"
                            data-testid={`flight-departure-input-${i}`}
                            value={f.departure_time}
                            onChange={(e) => setFlight(f.id, "departure_time", e.target.value)}
                          />
                        </div>
                        <div className="grid gap-1">
                          <span className="text-xs text-slate-500">Llegada</span>
                          <Input
                            type="time"
                            value={f.arrival_time}
                            onChange={(e) => setFlight(f.id, "arrival_time", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label>Notas de la reserva</Label>
              <Textarea
                value={res.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                placeholder="Observaciones..."
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button data-testid="save-reservation-button" onClick={save} disabled={saving}>
            {saving ? "Guardando..." : "Guardar Reserva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
